import { Icon, IconProps, Tag, TagColor, Tooltip } from '@shinetools/lumen-react'
import { ClockIcon, RefreshCwIcon, RefreshCwOff } from 'lucide-react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { CegidIntegrationStatus } from '@features/cegidIntegration/enums/cegidIntegrationStatus'

import { BillsTableRow } from '../../types/billsTableRow'
import * as S from './styles'

type StatusConfig = {
  color: TagColor
  icon: IconProps['icon']
  labelKey: string
  tooltipKey: string
}

const statusToConfigMapping: Record<CegidIntegrationStatus, StatusConfig> = {
  [CegidIntegrationStatus.Failed]: {
    color: 'warning',
    icon: RefreshCwOff,
    labelKey: 'receipts.receipts_list.transfer_status.failed_sync',
    tooltipKey: 'receipts.receipts_list.transfer_status.tooltip.failed_sync',
  },
  [CegidIntegrationStatus.Pending]: {
    color: 'neutral',
    icon: ClockIcon,
    labelKey: 'receipts.receipts_list.transfer_status.pending_sync',
    tooltipKey: 'receipts.receipts_list.transfer_status.tooltip.pending_sync',
  },
  [CegidIntegrationStatus.Processing]: {
    color: 'neutral',
    icon: ClockIcon,
    labelKey: 'receipts.receipts_list.transfer_status.pending_sync',
    tooltipKey: 'receipts.receipts_list.transfer_status.tooltip.pending_sync',
  },
  [CegidIntegrationStatus.Posted]: {
    color: 'blue',
    icon: RefreshCwIcon,
    labelKey: 'receipts.receipts_list.transfer_status.posted',
    tooltipKey: 'receipts.receipts_list.transfer_status.tooltip.posted',
  },
}

interface BillsTableSyncStatusCellProps {
  row: BillsTableRow
  isIconOnly?: boolean
}

export const BillsTableSyncStatusCell = ({ row, isIconOnly }: BillsTableSyncStatusCellProps): ReactElement | null => {
  const { t } = useTranslation()

  if (!row.syncStatus) {
    return null
  }

  const config = statusToConfigMapping[row.syncStatus.status]

  // Run-time safety check for unknown statuses
  if (!config) {
    return null
  }

  // We need to redeclare it uppercase to be able to use it with both the Icon and Tag component APIs
  const StatusIconComponent = config.icon

  if (isIconOnly) {
    return (
      <Tooltip label={t(config.tooltipKey)}>
        <S.TooltipChildWrapper>
          <Icon icon={StatusIconComponent} size="small" color="var(--lumen-color-content-neutral-soft)" />
        </S.TooltipChildWrapper>
      </Tooltip>
    )
  }

  return (
    <Tooltip label={t(config.tooltipKey)}>
      <Tag icon={<StatusIconComponent />} color={config.color} type="subtle" label={t(config.labelKey)} />
    </Tooltip>
  )
}
